import React, { useState, useEffect, useCallback } from 'react';
import {
  getAllBeoordelaars,
  createBeoordelaar,
  updateBeoordelaar,
  resetBeoordelaarPassword,
  deleteBeoordelaar,
  uitvoerBeoordelaars
} from '../services/beoordelaar_services';
import { genereerWagwoord as generatePassword } from '../services/wagwoord';

const cardStyle = {
  marginBottom: '2rem',
  padding: '1rem',
  border: '1px solid #ddd',
  borderRadius: '8px',
  backgroundColor: '#f9f9f9'
};

const labelStyle = { display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' };

const inputStyle = {
  width: '100%',
  padding: '0.5rem',
  border: '1px solid #ccc',
  borderRadius: '4px',
  fontSize: '1rem',
  boxSizing: 'border-box'
};

const buttonStyle = (backgroundColor, disabled = false) => ({
  padding: '0.5rem 1rem',
  backgroundColor,
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.6 : 1,
  fontSize: '0.95rem',
  whiteSpace: 'nowrap'
});

// The Wi-Fi network name and security are remembered in this browser (not the password)
const WIFI_SLEUTEL = 'melktert_wifi';
const leesWifi = () => {
  try {
    const gestoor = JSON.parse(localStorage.getItem(WIFI_SLEUTEL) || '{}');
    return { netwerk: gestoor.netwerk || '', sekuriteit: gestoor.sekuriteit || 'WPA' };
  } catch {
    return { netwerk: '', sekuriteit: 'WPA' };
  }
};
const stoorWifi = (netwerk, sekuriteit) => {
  try {
    localStorage.setItem(WIFI_SLEUTEL, JSON.stringify({ netwerk, sekuriteit }));
  } catch {
    // not available (e.g. private window): just don't remember it
  }
};

// Download a PDF with every beoordelaar's sign-in details, a sign-in QR code and a Wi-Fi QR
// code. Passwords are only stored hashed, so every beoordelaar gets a new password.
function UitvoerKaart({ aantal }) {
  const [wifi] = useState(leesWifi);
  const [netwerk, setNetwerk] = useState(wifi.netwerk);
  const [wifiWagwoord, setWifiWagwoord] = useState('');
  const [sekuriteit, setSekuriteit] = useState(wifi.sekuriteit);
  const [besig, setBesig] = useState(false);
  const [boodskap, setBoodskap] = useState('');
  const [fout, setFout] = useState('');

  const laaiAf = async () => {
    if (!window.confirm(`Elke beoordelaar (${aantal}) kry 'n nuwe wagwoord vir die PDF. Wagwoorde wat reeds uitgedeel is, werk dan nie meer nie. Gaan voort?`)) return;
    setBesig(true);
    setFout('');
    setBoodskap('');
    try {
      stoorWifi(netwerk.trim(), sekuriteit);
      const beoordelaars = await uitvoerBeoordelaars();
      // Load the PDF code only when it's needed
      const { laaiBeoordelaarsPdfAf } = await import('./uitvoer_pdf');
      await laaiBeoordelaarsPdfAf(beoordelaars, { netwerk: netwerk.trim(), wagwoord: wifiWagwoord, sekuriteit });
      setBoodskap(`PDF afgelaai met ${beoordelaars.length} beoordelaars, elk met 'n nuwe wagwoord.`);
    } catch (err) {
      setFout(err.message);
    } finally {
      setBesig(false);
    }
  };

  return (
    <div style={cardStyle}>
      <h3 style={{ marginTop: 0, color: '#0e1e3b' }}>Voer beoordelaars uit (PDF)</h3>
      <p style={{ marginTop: 0, color: '#666' }}>
        Een strokie per beoordelaar met hul gebruikersnaam en wagwoord, 'n QR-kode wat hulle aanmeld en 'n QR-kode vir
        die Wi-Fi. <strong>Elke beoordelaar kry 'n nuwe wagwoord</strong>: wagwoorde word nie leesbaar gestoor nie, so
        die vorige wagwoorde werk daarna nie meer nie. Beoordelaars wat reeds aangemeld is, bly aangemeld.
      </p>
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '1rem' }}>
        <div style={{ flex: '1 1 200px' }}>
          <label htmlFor="wifi-netwerk" style={labelStyle}>Wi-Fi netwerk</label>
          <input id="wifi-netwerk" type="text" value={netwerk} onChange={(e) => setNetwerk(e.target.value)}
            placeholder="bv. Akademia-Gaste" autoComplete="off" style={inputStyle} />
        </div>
        <div style={{ flex: '1 1 200px' }}>
          <label htmlFor="wifi-wagwoord" style={labelStyle}>Wi-Fi wagwoord</label>
          <input id="wifi-wagwoord" type="text" value={wifiWagwoord} onChange={(e) => setWifiWagwoord(e.target.value)}
            disabled={sekuriteit === 'nopass'} autoComplete="off" autoCapitalize="none" spellCheck={false} style={inputStyle} />
        </div>
        <div style={{ flex: '0 1 160px' }}>
          <label htmlFor="wifi-sekuriteit" style={labelStyle}>Sekuriteit</label>
          <select id="wifi-sekuriteit" value={sekuriteit} onChange={(e) => setSekuriteit(e.target.value)} style={inputStyle}>
            <option value="WPA">WPA/WPA2/WPA3</option>
            <option value="WEP">WEP</option>
            <option value="nopass">Oop (geen wagwoord)</option>
          </select>
        </div>
      </div>
      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <button type="button" onClick={laaiAf} disabled={besig || aantal === 0} style={buttonStyle('#0e1e3b', besig || aantal === 0)}>
          {besig ? 'Maak PDF...' : 'Laai PDF af'}
        </button>
        {!netwerk.trim() && <span style={{ color: '#666' }}>Sonder 'n netwerknaam is daar net die aanmeld-QR-kode.</span>}
      </div>
      {boodskap && <p style={{ color: '#1e6b35', marginBottom: 0 }}>{boodskap}</p>}
      {fout && <p style={{ color: '#a32020', marginBottom: 0 }}>Fout: {fout}</p>}
    </div>
  );
}

function Beoordelaars() {
  const [beoordelaars, setBeoordelaars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState(generatePassword);
  const [volleNaam, setVolleNaam] = useState('');
  const [organisasie, setOrganisasie] = useState('');
  const [epos, setEpos] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [busyUser, setBusyUser] = useState(null); // beoordelaar with a reset/delete in progress
  const [credentials, setCredentials] = useState(null); // { username, password, isNew } to share
  const [copied, setCopied] = useState(false);
  const [editingUser, setEditingUser] = useState(null); // beoordelaar being edited
  const [editValues, setEditValues] = useState({ volle_naam: '', organisasie: '', epos: '' });
  const [resettingUser, setResettingUser] = useState(null); // beoordelaar getting a new password
  const [resetPasswordValue, setResetPasswordValue] = useState('');

  const loadBeoordelaars = useCallback(async () => {
    try {
      const data = await getAllBeoordelaars();
      setBeoordelaars(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBeoordelaars();
  }, [loadBeoordelaars]);

  const showCredentials = (user, pw, isNew) => {
    setCredentials({ username: user, password: pw, isNew });
    setCopied(false);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    try {
      const created = await createBeoordelaar(username, password, { volle_naam: volleNaam, organisasie, epos });
      showCredentials(created.username, password, true);
      setUsername('');
      setPassword(generatePassword());
      setVolleNaam('');
      setOrganisasie('');
      setEpos('');
      await loadBeoordelaars();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditStart = (b) => {
    setEditingUser(b.username);
    setEditValues({
      volle_naam: b.volle_naam || '',
      organisasie: b.organisasie || '',
      epos: b.epos || ''
    });
  };

  const handleEditSave = async () => {
    setError('');
    setBusyUser(editingUser);
    try {
      await updateBeoordelaar(editingUser, editValues);
      setEditingUser(null);
      await loadBeoordelaars();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyUser(null);
    }
  };

  const handleResetStart = (user) => {
    setResettingUser(user);
    setResetPasswordValue(generatePassword());
  };

  const handleResetPassword = async (user) => {
    if (resetPasswordValue.length < 6) {
      setError("Die wagwoord moet minstens 6 karakters wees.");
      return;
    }
    setError('');
    setBusyUser(user);
    try {
      await resetBeoordelaarPassword(user, resetPasswordValue);
      showCredentials(user, resetPasswordValue, false);
      setResettingUser(null);
      setResetPasswordValue('');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyUser(null);
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Verwyder beoordelaar '${user}'?`)) return;
    setError('');
    setBusyUser(user);
    try {
      await deleteBeoordelaar(user);
      if (credentials?.username === user) setCredentials(null);
      await loadBeoordelaars();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyUser(null);
    }
  };

  const credentialsText = credentials
    ? `Melktert: ${window.location.origin}\nGebruikersnaam: ${credentials.username}\nWagwoord: ${credentials.password}`
    : '';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(credentialsText);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div style={{ padding: '2rem', maxWidth: '900px', margin: '0 auto' }}>
      <h2 style={{ color: '#0e1e3b' }}>Beoordelaars</h2>
      <p style={{ color: '#666', marginBottom: '2rem' }}>
        Skep 'n rekening vir elke beoordelaar wanneer hulle opdaag en deel die gebruikersnaam en wagwoord met hulle.
        Die wagwoord word net nou gewys - as 'n beoordelaar dit vergeet, gee vir hulle 'n nuwe wagwoord.
      </p>

      {error && (
        <div style={{
          padding: '1rem',
          backgroundColor: '#f8d7da',
          color: '#721c24',
          border: '1px solid #f5c6cb',
          borderRadius: '4px',
          marginBottom: '1rem'
        }}>
          Fout: {error}
        </div>
      )}

      {/* Credentials to share with the beoordelaar - only shown right after creating or resetting */}
      {credentials && (
        <div style={{ ...cardStyle, backgroundColor: '#fffaf0', border: '2px solid #77502d' }}>
          <h3 style={{ marginTop: 0, color: '#77502d' }}>
            {credentials.isNew ? 'Beoordelaar geskep' : 'Nuwe wagwoord gestel'} - deel dit met {credentials.username}
          </h3>
          <div style={{ fontFamily: 'monospace', fontSize: '1.2rem', lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>
            {credentialsText}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', marginTop: '1rem' }}>
            {navigator.clipboard && (
              <button type="button" onClick={handleCopy} style={buttonStyle('#0e1e3b')}>
                {copied ? 'Gekopieer!' : 'Kopieer'}
              </button>
            )}
            <button type="button" onClick={() => setCredentials(null)} style={buttonStyle('#77502d')}>
              Klaar
            </button>
          </div>
        </div>
      )}

      {/* New beoordelaar form */}
      <form onSubmit={handleCreate} style={cardStyle}>
        <h3 style={{ marginTop: 0, color: '#0e1e3b' }}>Nuwe beoordelaar</h3>
        <div style={{ marginBottom: '1rem' }}>
          <label htmlFor="beoordelaar-username" style={labelStyle}>
            Gebruikersnaam *
          </label>
          <input
            type="text"
            id="beoordelaar-username"
            value={username}
            onChange={(e) => setUsername(e.target.value.toLowerCase())}
            required
            autoCapitalize="none"
            autoCorrect="off"
            autoComplete="off"
            spellCheck={false}
            style={inputStyle}
            placeholder="bv. jan"
          />
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <label htmlFor="beoordelaar-volle-naam" style={labelStyle}>
            Volle naam
          </label>
          <input
            type="text"
            id="beoordelaar-volle-naam"
            value={volleNaam}
            onChange={(e) => setVolleNaam(e.target.value)}
            style={inputStyle}
            placeholder="bv. Jan van der Merwe"
          />
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <label htmlFor="beoordelaar-organisasie" style={labelStyle}>
            Organisasie
          </label>
          <input
            type="text"
            id="beoordelaar-organisasie"
            value={organisasie}
            onChange={(e) => setOrganisasie(e.target.value)}
            style={inputStyle}
            placeholder="bv. Universiteit Stellenbosch"
          />
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <label htmlFor="beoordelaar-epos" style={labelStyle}>
            E-pos
          </label>
          <input
            type="email"
            id="beoordelaar-epos"
            value={epos}
            onChange={(e) => setEpos(e.target.value)}
            style={inputStyle}
            placeholder="bv. jan@voorbeeld.co.za"
          />
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <label htmlFor="beoordelaar-password" style={labelStyle}>
            Wagwoord *
          </label>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {/* Plain text on purpose: the admin reads it out or shares it with the beoordelaar */}
            <input
              type="text"
              id="beoordelaar-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              autoCapitalize="none"
              autoCorrect="off"
              autoComplete="off"
              spellCheck={false}
              style={{ ...inputStyle, fontFamily: 'monospace' }}
            />
            <button type="button" onClick={() => setPassword(generatePassword())} style={buttonStyle('#77502d')}>
              Genereer
            </button>
          </div>
        </div>
        <button type="submit" disabled={isSubmitting} style={buttonStyle('#0e1e3b', isSubmitting)}>
          {isSubmitting ? 'Skep...' : 'Skep beoordelaar'}
        </button>
      </form>

      <UitvoerKaart aantal={beoordelaars.length} />

      {/* Existing beoordelaars */}
      <h3 style={{ color: '#0e1e3b' }}>Bestaande beoordelaars ({beoordelaars.length})</h3>
      {loading ? (
        <div style={{ padding: '2rem' }}>Laai beoordelaars...</div>
      ) : beoordelaars.length === 0 ? (
        <div style={{ padding: '2rem', color: '#666', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
          Nog geen beoordelaars nie. Skep die eerste een hierbo.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '0.5rem' }}>
          {beoordelaars.map(b => {
            const busy = busyUser === b.username;
            const hasScored = b.spanne_gemerk > 0;
            return (
              <div
                key={b.username}
                style={{
                  padding: '1rem',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  backgroundColor: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '1rem',
                  flexWrap: 'wrap'
                }}
              >
                {editingUser === b.username ? (
                  <div style={{ textAlign: 'left', flex: '1 1 260px', display: 'grid', gap: '0.5rem' }}>
                    <strong style={{ color: '#0e1e3b', fontSize: '1.1rem' }}>{b.username}</strong>
                    <input
                      type="text"
                      value={editValues.volle_naam}
                      onChange={(e) => setEditValues({ ...editValues, volle_naam: e.target.value })}
                      placeholder="Volle naam"
                      style={{ ...inputStyle, width: 'auto' }}
                    />
                    <input
                      type="text"
                      value={editValues.organisasie}
                      onChange={(e) => setEditValues({ ...editValues, organisasie: e.target.value })}
                      placeholder="Organisasie"
                      style={{ ...inputStyle, width: 'auto' }}
                    />
                    <input
                      type="email"
                      value={editValues.epos}
                      onChange={(e) => setEditValues({ ...editValues, epos: e.target.value })}
                      placeholder="E-pos"
                      style={{ ...inputStyle, width: 'auto' }}
                    />
                  </div>
                ) : (
                  <div style={{ textAlign: 'left' }}>
                    <strong style={{ color: '#0e1e3b', fontSize: '1.1rem' }}>{b.username}</strong>
                    {b.volle_naam && (
                      <span style={{ color: '#0e1e3b', marginLeft: '8px' }}>{b.volle_naam}</span>
                    )}
                    <div style={{ fontSize: '0.9rem', color: '#666' }}>
                      {b.organisasie ? `${b.organisasie} · ` : ''}
                      {b.spanne_gemerk} {b.spanne_gemerk === 1 ? 'span' : 'spanne'} gemerk
                    </div>
                    {b.epos && <div style={{ fontSize: '0.8rem', color: '#888' }}>{b.epos}</div>}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {resettingUser === b.username ? (
                    <>
                      {/* The admin types or generates the new password, then sets it */}
                      <input
                        type="text"
                        value={resetPasswordValue}
                        onChange={(e) => setResetPasswordValue(e.target.value)}
                        minLength={6}
                        autoCapitalize="none"
                        autoCorrect="off"
                        autoComplete="off"
                        spellCheck={false}
                        style={{ ...inputStyle, width: 'auto', fontFamily: 'monospace' }}
                      />
                      <button
                        type="button"
                        onClick={() => setResetPasswordValue(generatePassword())}
                        disabled={busy}
                        style={buttonStyle('#77502d', busy)}
                      >
                        Genereer
                      </button>
                      <button
                        type="button"
                        onClick={() => handleResetPassword(b.username)}
                        disabled={busy}
                        style={buttonStyle('#0e1e3b', busy)}
                      >
                        Stel wagwoord
                      </button>
                      <button
                        type="button"
                        onClick={() => { setResettingUser(null); setResetPasswordValue(''); }}
                        disabled={busy}
                        style={buttonStyle('#999', busy)}
                      >
                        Kanselleer
                      </button>
                    </>
                  ) : editingUser === b.username ? (
                    <>
                      <button
                        type="button"
                        onClick={handleEditSave}
                        disabled={busy}
                        style={buttonStyle('#0e1e3b', busy)}
                      >
                        Stoor
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingUser(null)}
                        disabled={busy}
                        style={buttonStyle('#999', busy)}
                      >
                        Kanselleer
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => handleEditStart(b)}
                        disabled={busy}
                        style={buttonStyle('#0e1e3b', busy)}
                      >
                        Wysig
                      </button>
                      <button
                        type="button"
                        onClick={() => handleResetStart(b.username)}
                        disabled={busy}
                        style={buttonStyle('#0e1e3b', busy)}
                      >
                        Nuwe wagwoord
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(b.username)}
                        disabled={busy || hasScored}
                        title={hasScored ? 'Kan nie verwyder word nie: het reeds punte gegee' : 'Verwyder beoordelaar'}
                        style={buttonStyle('#77502d', busy || hasScored)}
                      >
                        Verwyder
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default Beoordelaars;
